import { GLOBAL, getUserAgent, getRequestId } from 'saddlebag-browser';
import { SingleValueCookie } from 'saddlebag-cookie';
import { getReconciledReferrer, safeTrim } from 'saddlebag-grappler';
import { logError } from 'saddlebag-logger';
import {
  getPreferences,
  PREFERENCES,
  isGdprInitialised,
  isGdprInitSessionActive,
} from 'saddlebag-tracking-preferences';
import { FunnelEventsTracking } from 'saddlebag-user-tracking-events';
import { extractParams, getURLTrackingParams } from 'saddlebag-view-tracking';

import { getPlatform, getSkyscannerModifiers } from './experimentUserContext';
import { redactUrl } from './redactUrl';

let funnelTracker;
let lastSentUserPreferences = null;
let lastSentAllocationCookie = null;

const getFunnelTracker = (data) => {
  if (funnelTracker) return funnelTracker;

  const env = (data.config && data.config.env) || 'public';
  funnelTracker = new FunnelEventsTracking(env, data.microsite.name, {
    enhance: false,
  });
  return funnelTracker;
};

const sendEvent = async (data, eventName, event, opts = {}) => {
  const tracker = getFunnelTracker(data);

  try {
    await tracker.track(eventName, event, opts);
  } catch (err) {
    logError(err, {
      component: 'funnelEvents',
    });
  }
};

const sendFunnelClientsViewEvent = async ({
  allowedQueryParams,
  analyticsPageName,
  analyticsPageUrl,
  config,
  culture,
  isInternalUser,
  micrositeName,
  pageInstance,
  pageType,
  previousName,
  referrerId,
  requestId,
  viewGuid,
  virtualReferrer,
  webNodeOverride,
}) => {
  try {
    const $window = GLOBAL.getWindow();
    const $document = GLOBAL.getDocument();
    /*
      A hash fragment is a client side concept; browsers do not transmit it to the server.
      Taking advantage of this sometimes sensitive tokens are appended after a fragment, relying on them not being readable server side.
      It was seen before pull/63 that tokens were being appended this way and leaking as we were logging the raw URL.
      To avoid this risk we strip the hash fragment from the href.
      document.referrer is not vulnerable in the same way; the browser already excludes hash fragments when calculating the referrer.

      We also provide an optional allowedQueryParams list for individual consumers to specify which query params they want to allow through.
      There are no known current consumers and it could potentially be removed.
    */
    const currentUrl =
      $window.location &&
      redactUrl($window.location.href.replace(/#.*/, ''), {
        allowedQueryParams,
      });

    const existingParams = extractParams(currentUrl);

    const referrerUrl = getReconciledReferrer(virtualReferrer);

    const opts = viewGuid ? { messageGuid: viewGuid } : {};

    const viewData = {
      name: safeTrim(analyticsPageName),
      previous_name: previousName,
      referrer_url: referrerUrl,
      referrer_id: referrerId,
      document_referrer: $document.referrer,
      is_internal_user: isInternalUser,
      page_instance: pageInstance,
      request_id: safeTrim(requestId || getRequestId()),
      url_tracking_params: getURLTrackingParams(existingParams),
      url: safeTrim(currentUrl),
      virtual_referrer: virtualReferrer || '',
      web_node: webNodeOverride || micrositeName,
      page_group: safeTrim(pageType),
      market: culture.market,
      locale: culture.locale,
      currency: culture.currency,
      analytics_page_url: safeTrim(analyticsPageUrl),
    };

    await sendEvent(
      { config, microsite: { name: micrositeName } },
      'clients.View',
      viewData,
      opts,
    );
  } catch (err) {
    logError(err, {
      component: 'trackolding_sendFunnelClientsViewEvent',
    });
  }
};

const isInternalReferral = () => {
  try {
    const referrerUrl = new URL(GLOBAL.getDocument().referrer);
    const currentUrl = new URL(GLOBAL.getDocument().documentURI);

    if (!referrerUrl || !currentUrl) {
      return false;
    }

    const currentHostname = currentUrl.hostname.toLowerCase();
    const referrerHostname = referrerUrl.hostname.toLowerCase();

    return currentHostname === referrerHostname;
  } catch (err) {
    return false;
  }
};

const getDeviceCategory = (device) => {
  switch (true) {
    case device.isBrowser:
      return 'DESKTOP';
    case device.isMobilePhone:
      return 'MOBILE';
    case device.isTablet:
      return 'TABLET';
    default:
      return 'UNSET_DEVICE_CATEGORY';
  }
};

const sendFunnelAcquisitionEvent = async (data) => {
  if (isInternalReferral() || data.virtualReferrer) {
    return;
  }
  const device = data.device || {};

  const acquisitionEvent = {
    platform_type: 'WEB',
    acquisition_type: 'WEB_SESSION',
    referral_url: GLOBAL.getDocument().referrer,
    user_agent_string: getUserAgent(),
    device_category: getDeviceCategory(device),
    os_name: device.osName || '',
    os_version: device.osVersion || '',
    is_browser: device.isBrowser === true,
    browser_name: device.browserName || '',
    browser_version: device.browserVersion || '',
    model_name: device.model || '',
    vendor_name: device.vendor || '',
    marketing_name: device.marketingName || '',
    is_robot: device.isRobot === true,
    primary_hardware_type: device.primaryHardwareType || '',
  };

  await sendEvent(data, 'clients.Acquisition', acquisitionEvent);
};

// Travellers fall under three categories
// First request pre-consent - Event emitted
// Accepted analytics consent - Event emitted
// Declined analytics consent - Event suppressed
//
// We compare the current experiment_allocation_id cookie value against
// lastSentAllocationCookie, the value we last emitted for. If they match we've
// already reported this state, so emitting again would be a duplicate.
const shouldEmitExperimentUserContextEvent = (
  resolvedJhaToken,
  allocationCookie,
  jhaCookie,
) => {
  // if we can resolve the jha token but the experiment_allocation_id cookie
  // is not written to the response cookies we are on a pre-consent request
  if (resolvedJhaToken !== '' && allocationCookie === '') {
    // emit if it's not a duplicate
    return allocationCookie !== lastSentAllocationCookie;
  }

  // if we have written the jha and experiment_allocation_id cookies we have accepted consent
  if (jhaCookie !== '' && allocationCookie !== '') {
    // emit if it's not a duplicate
    return allocationCookie !== lastSentAllocationCookie;
  }

  // otherwise we have declined consent
  return false;
};

const sendExperimentUserContextEvent = async (data) => {
  // Only emit events if emitExperimentAllocationEventInTrackolding is true
  if (data.config?.emitExperimentAllocationEventInTrackolding) {
    const schema = 'experiment_user_context.ExperimentUserContext';

    const jhaCookie = new SingleValueCookie('jha').getValue() || '';
    const allocationCookie =
      new SingleValueCookie('experiment_allocation_id').getValue() || '';
    const resolvedJhaToken = data.jhaToken || '';

    if (
      !shouldEmitExperimentUserContextEvent(
        resolvedJhaToken,
        allocationCookie,
        jhaCookie,
      )
    ) {
      return;
    }

    const ssabOverrides = new SingleValueCookie('ssaboverrides').getValue();

    const culture = data.culture || {};
    const experimentUserContextEvent = {
      market: culture.market || '',
      locale: culture.locale || '',
      platform: getPlatform(data),
      currency: culture.currency || '',
      skyscanner_modifiers: getSkyscannerModifiers(data),
      flight_provider_id: data.flightProviderId || '',
      utm_medium: data.utmMedium || '',
      web_context: {
        page_type: data.pageType || '',
      },
      ssab_overrides: ssabOverrides || '',
    };

    await sendEvent(data, schema, experimentUserContextEvent, {
      jhaToken: resolvedJhaToken,
    });

    lastSentAllocationCookie = allocationCookie;
  }
};

const sendUserPreferencesEvent = async (data) => {
  const schema = 'clients.UserPreferences';

  const trackingPreferences = getPreferences(data.culture.market);
  const isGdprInit = isGdprInitialised() || isGdprInitSessionActive();
  const userPreferences = {
    country: data.culture.market,
    currency: data.culture.currency,
    locale: data.culture.locale,
    is_optedin_for_personalised: trackingPreferences[PREFERENCES.ADVERTS],
    is_optedin_for_optimization: trackingPreferences[PREFERENCES.INFORMATION],
    is_gdpr_active: trackingPreferences.isGdprActiveInMarket,
    gdpr_cookie_version: trackingPreferences[PREFERENCES.VERSION],
    is_gdpr_initialised: isGdprInit,
  };

  const serialisedPreferences = JSON.stringify(userPreferences);
  if (serialisedPreferences === lastSentUserPreferences) {
    return;
  }

  getFunnelTracker(data);
  await sendEvent(data, schema, userPreferences);
  lastSentUserPreferences = serialisedPreferences;
};

// Resets module-level dedup state between tests. Not for production use —
// the state is intentionally persistent for the lifetime of the sync script.
const _resetDedupeState = () => {
  lastSentUserPreferences = null;
  lastSentAllocationCookie = null;
};

export {
  sendFunnelClientsViewEvent,
  sendFunnelAcquisitionEvent,
  sendExperimentUserContextEvent,
  sendUserPreferencesEvent,
  _resetDedupeState,
};
